import React, { useMemo } from 'react';
import {
    Box,
    Alert,
    Paper,
    Text,
    Title,
    Button,
    TextInput,
    Checkbox,
    Switch,
    Table,
    Tabs,
    Tooltip,
    ActionIcon,
    Loader,
    Group,
    Pagination,
    Select
} from '@mantine/core';
import {
    IconRefresh,
    IconAlertTriangle,
    IconCircleCheck,
    IconAlertCircle,
    IconX,
    IconSearch,
    IconPlayerPlay,
    IconInfoCircle,
    IconEye,
    IconCopy,
} from '@tabler/icons-react';
import { BulkGroupItem, BulkGroupStatus, ProcessingStats, ConversionStats } from '../types/GroupsManagementTypes';
import { copyToClipboard } from '../../../utils/clipboardUtils';

interface LegacyBulkTabProps {
    // State
    bulkGroups: BulkGroupItem[];
    selectedBulkGroups: Set<string>;
    bulkGroupsPage: number;
    bulkGroupsRowsPerPage: number;
    bulkGroupsTotalResults: number | null;
    loadingBulkGroups: boolean;
    processingBulkGroups: boolean;
    processingStats: ProcessingStats | null;
    conversionStats: ConversionStats | null;
    bulkGroupStatuses: Map<string, BulkGroupStatus>;
    editingConvertedFilter: string | null;
    editedConvertedFilters: Map<string, string>;
    bulkSubTab: number;
    excludeManaged: boolean;
    excludeHasFilter: boolean;
    excludeJobCodeMemberOf: boolean;
    bulkCustomFilter: string;
    effectiveFilter: string;
    legacySuccess: string | null;
    legacyError: string | null;

    // Setters
    setBulkGroupsPage: (page: number) => void;
    setBulkGroupsRowsPerPage: (rowsPerPage: number) => void;
    setEditingConvertedFilter: (id: string | null) => void;
    setEditedConvertedFilters: (filters: Map<string, string>) => void;
    setBulkSubTab: (tab: number) => void;
    setExcludeManaged: (exclude: boolean) => void;
    setExcludeHasFilter: (exclude: boolean) => void;
    setExcludeJobCodeMemberOf: (exclude: boolean) => void;
    setBulkCustomFilter: (filter: string) => void;
    setLegacySuccess: (msg: string | null) => void;
    setLegacyError: (msg: string | null) => void;

    // Handlers
    loadBulkGroups: (page: number, pageSize: number, excludeManagedGroups?: boolean, excludeHasFilterGroups?: boolean, excludeJobCodeMemberOfGroups?: boolean, customFilter?: string) => Promise<void>;
    handleSelectBulkGroup: (groupId: string, selected: boolean) => void;
    handleSelectAllBulkGroups: (selected: boolean) => void;
    handleProcessBulkGroups: () => Promise<void>;
    handleCancelBulkProcessing: () => void;
    handleReloadBulkGroups: () => Promise<void>;
    handleUpdateFilters: () => Promise<void>;
    handleRefreshRow: (groupId: string) => Promise<void>;
    handleEditedFilterBlur: (groupId: string) => Promise<void>;
    onViewDetails?: (groupId: string) => void;
}

export const LegacyBulkTab: React.FC<LegacyBulkTabProps> = ({
    bulkGroups,
    selectedBulkGroups,
    bulkGroupsPage,
    bulkGroupsRowsPerPage,
    bulkGroupsTotalResults,
    loadingBulkGroups,
    processingBulkGroups,
    processingStats,
    conversionStats,
    bulkGroupStatuses,
    editingConvertedFilter,
    editedConvertedFilters,
    bulkSubTab,
    excludeManaged,
    excludeHasFilter,
    excludeJobCodeMemberOf,
    bulkCustomFilter,
    effectiveFilter,
    legacySuccess,
    legacyError,
    setBulkGroupsPage,
    setBulkGroupsRowsPerPage,
    setEditingConvertedFilter,
    setEditedConvertedFilters,
    setBulkSubTab,
    setExcludeManaged,
    setExcludeHasFilter,
    setExcludeJobCodeMemberOf,
    setBulkCustomFilter,
    setLegacySuccess,
    setLegacyError,
    loadBulkGroups,
    handleSelectBulkGroup,
    handleSelectAllBulkGroups,
    handleProcessBulkGroups,
    handleCancelBulkProcessing,
    handleReloadBulkGroups,
    handleUpdateFilters,
    handleRefreshRow,
    handleEditedFilterBlur,
    onViewDetails
}) => {
    const handleBulkGroupsPageChange = (_event: unknown, newPage: number) => {
        setBulkGroupsPage(newPage);
        loadBulkGroups(newPage, bulkGroupsRowsPerPage, excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter);
    };

    const handleBulkGroupsRowsPerPageChange = (value: string | null) => {
        const newRowsPerPage = parseInt(value || '25', 10);
        setBulkGroupsRowsPerPage(newRowsPerPage);
        setBulkGroupsPage(0);
        loadBulkGroups(0, newRowsPerPage, excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter);
    };

    const handleBulkSearch = () => {
        setBulkGroupsPage(0);
        loadBulkGroups(0, bulkGroupsRowsPerPage, excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter);
    };

    const handleCopyMemberIds = (members: any[]) => {
        const memberIds = (members || []).map((memberId: any) =>
            typeof memberId === 'string' ? memberId : (memberId.value || memberId.Id || memberId.id || memberId)
        );
        copyToClipboard(memberIds.join('\n'));
    };

    const processingIndicator = useMemo(() => {
        const groupsStillProcessing = bulkGroups.filter(g => {
            if (!g.legacyFilter) return false;
            const stillLoading = g._loading?.converted || g._loading?.optimized || g._loading?.counts;
            const stillTesting = g.filterTestWarning === 'Testing...';
            return stillLoading || stillTesting;
        });

        const totalStillProcessing = groupsStillProcessing.length;
        const totalGroups = bulkGroups.length;
        const completed = totalGroups - totalStillProcessing;

        const currentlyConvertingFilters = bulkGroups.filter(g =>
            g._loading?.converted || g._loading?.optimized
        ).length;
        const currentlyPreviewingCounts = bulkGroups.filter(g =>
            g._loading?.counts || g.filterTestWarning === 'Testing...'
        ).length;
        const currentlyProcessing = Array.from(bulkGroupStatuses.values()).filter(s =>
            s.status === 'processing'
        ).length;

        const hasActiveWork = totalStillProcessing > 0 || loadingBulkGroups || currentlyProcessing > 0;

        return {
            totalStillProcessing,
            totalGroups,
            completed,
            currentlyConvertingFilters,
            currentlyPreviewingCounts,
            currentlyProcessing,
            hasActiveWork
        };
    }, [bulkGroups, bulkGroupStatuses, loadingBulkGroups]);

    const selectAllState = useMemo(() => {
        const filteredGroups = excludeManaged ? bulkGroups.filter(g => !g.isManaged) : bulkGroups;
        const selectableGroups = filteredGroups.filter(g => {
            const count = g.filterTestGroupCount ?? g.optimizedFilterCount;
            return count !== undefined || (g.filterTestWarning && g.filterTestWarning !== 'Testing...');
        });
        const allSelected = selectableGroups.length > 0 && selectableGroups.every(g => selectedBulkGroups.has(g.id));
        const someSelected = selectedBulkGroups.size > 0 && selectedBulkGroups.size < selectableGroups.length;
        return { allSelected, someSelected, selectableGroups };
    }, [bulkGroups, excludeManaged, selectedBulkGroups]);

    const rowsPerPageOptions = [
        { value: '10', label: '10' },
        { value: '20', label: '20' },
        { value: '25', label: '25' },
        { value: '40', label: '40' },
        { value: '50', label: '50' },
        { value: '100', label: '100' },
        { value: '200', label: '200' },
        { value: '400', label: '400' },
    ];

    const totalPages = bulkGroupsTotalResults != null
        ? Math.max(1, Math.ceil(bulkGroupsTotalResults / bulkGroupsRowsPerPage))
        : 1;

    return (
        <Box>
            {legacySuccess && (
                <Alert color="green" mb="md" withCloseButton onClose={() => setLegacySuccess(null)}>
                    {legacySuccess}
                </Alert>
            )}
            {legacyError && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setLegacyError(null)}>
                    {legacyError}
                </Alert>
            )}

            <Paper withBorder p="md" mb="lg">
                <Title order={5} mb="xs">
                    Legacy Bulk Group Converter
                </Title>
                <Text size="sm" c="dimmed" mb="md">
                    Select groups with LegacyFilter to convert them to dynamic groups. Check the groups you want to process and click "Convert Groups" to update them.
                </Text>

                {processingStats && processingStats.processingTimeMs !== null && (
                    <Alert color="blue" mb="md">
                        <Text size="sm">
                            <strong>Processing Complete:</strong> {processingStats.itemsProcessed} items processed in {(processingStats.processingTimeMs / 1000).toFixed(2)}s
                            {processingStats.itemsProcessed > 0 && (
                                <> (avg {(processingStats.processingTimeMs / processingStats.itemsProcessed).toFixed(0)}ms per item)</>
                            )}
                            {processingStats.itemsNotInCache > 0 && (
                                <> • {processingStats.itemsNotInCache} not in cache</>
                            )}
                        </Text>
                    </Alert>
                )}

                {conversionStats && conversionStats.itemsConverted > 0 && conversionStats.conversionTimeMs !== null && (
                    <Alert color="green" mb="md">
                        <Text size="sm">
                            <strong>Conversion Complete:</strong> {conversionStats.itemsConverted} items converted in {(conversionStats.conversionTimeMs / 1000).toFixed(2)}s
                            {conversionStats.itemsConverted > 0 && (
                                <> (avg {(conversionStats.conversionTimeMs / conversionStats.itemsConverted).toFixed(0)}ms per item)</>
                            )}
                        </Text>
                    </Alert>
                )}

                <Tabs value={bulkSubTab.toString()} onChange={(value) => setBulkSubTab(parseInt(value || '0', 10))} mb="md">
                    <Tabs.List>
                        <Tabs.Tab value="0">Groups</Tabs.Tab>
                        <Tabs.Tab value="1">Raw Views</Tabs.Tab>
                    </Tabs.List>
                </Tabs>

                {bulkSubTab === 0 ? (
                    <>
                        <Box mb="md">
                            <TextInput
                                w="100%"
                                label="Filter"
                                placeholder="e.g., DisplayName co 'Test'"
                                value={bulkCustomFilter}
                                onChange={(e) => setBulkCustomFilter(e.currentTarget.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleBulkSearch();
                                    }
                                }}
                                size="sm"
                                rightSection={
                                    <Group gap="xs">
                                        <ActionIcon
                                            size="sm"
                                            onClick={handleBulkSearch}
                                            disabled={loadingBulkGroups}
                                        >
                                            {loadingBulkGroups ? <Loader size={16} /> : <IconSearch size={16} />}
                                        </ActionIcon>
                                    </Group>
                                }
                            />
                            <Paper withBorder p="sm" mt="xs" bg="var(--mantine-color-default-hover)">
                                <Text size="xs" c="dimmed" component="span" style={{ display: 'block' }} mb={4}>
                                    <strong>Effective Query:</strong>
                                </Text>
                                <Text size="sm" component="pre" ff="monospace" fz="xs" m={0} style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}>
                                    {effectiveFilter}
                                </Text>
                                {bulkGroupsTotalResults !== null && (
                                    <Text size="xs" c="dimmed" component="span" style={{ display: 'block' }} mt={4}>
                                        Total Results: <strong>{bulkGroupsTotalResults}</strong> •
                                        Showing: <strong>{bulkGroups.length}</strong> of <strong>{bulkGroupsTotalResults}</strong> •
                                        Page: <strong>{bulkGroupsPage + 1}</strong> •
                                        Rows per page: <strong>{bulkGroupsRowsPerPage}</strong>
                                    </Text>
                                )}
                            </Paper>
                        </Box>

                        <Group justify="flex-end" align="center" mb="md">
                            <Group align="center" gap="xs">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleReloadBulkGroups}
                                    disabled={loadingBulkGroups}
                                    leftSection={loadingBulkGroups ? <Loader size={16} /> : <IconRefresh size={16} />}
                                >
                                    Reload
                                </Button>
                                <Switch
                                    label="Exclude Managed"
                                    checked={excludeManaged}
                                    onChange={(e) => setExcludeManaged(e.currentTarget.checked)}
                                />
                                <Switch
                                    label="Exclude Has Filter"
                                    checked={excludeHasFilter}
                                    onChange={(e) => setExcludeHasFilter(e.currentTarget.checked)}
                                />
                                <Switch
                                    label="Exclude JobCodeMemberOf"
                                    checked={excludeJobCodeMemberOf}
                                    onChange={(e) => setExcludeJobCodeMemberOf(e.currentTarget.checked)}
                                />
                                {bulkGroups.filter(g => selectedBulkGroups.has(g.id) && g.needsFilterUpdate).length > 0 && !processingBulkGroups && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleUpdateFilters}
                                        disabled={selectedBulkGroups.size === 0}
                                        leftSection={<IconRefresh size={16} />}
                                    >
                                        Update Filters ({bulkGroups.filter(g => selectedBulkGroups.has(g.id) && g.needsFilterUpdate).length})
                                    </Button>
                                )}
                                {processingBulkGroups ? (
                                    <Button
                                        color="red"
                                        size="sm"
                                        onClick={handleCancelBulkProcessing}
                                        leftSection={<IconX size={16} />}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        onClick={handleProcessBulkGroups}
                                        disabled={selectedBulkGroups.size === 0}
                                        leftSection={<IconPlayerPlay size={16} />}
                                    >
                                        Convert Groups ({selectedBulkGroups.size})
                                    </Button>
                                )}
                            </Group>
                        </Group>

                        {bulkGroupsTotalResults !== null && (
                            <Group mb="md" gap="md" align="center">
                                <Select
                                    size="xs"
                                    value={bulkGroupsRowsPerPage.toString()}
                                    onChange={handleBulkGroupsRowsPerPageChange}
                                    data={rowsPerPageOptions}
                                    w={80}
                                />
                                <Pagination
                                    total={totalPages}
                                    value={bulkGroupsPage + 1}
                                    onChange={(page) => handleBulkGroupsPageChange(null, page - 1)}
                                    size="sm"
                                    withEdges
                                />
                            </Group>
                        )}

                        {processingIndicator.hasActiveWork && (
                            <Alert color="blue" mb="md">
                                <Group gap="xs" align="center">
                                    <Loader size={16} />
                                    <Text size="sm">
                                        {loadingBulkGroups ? 'Loading' : 'Updating'}: <strong>{processingIndicator.totalStillProcessing}</strong> of <strong>{processingIndicator.totalGroups}</strong> remaining
                                        {processingIndicator.completed > 0 && ` (${processingIndicator.completed} completed)`}
                                        {processingIndicator.currentlyConvertingFilters > 0 && ` • ${processingIndicator.currentlyConvertingFilters} converting filters`}
                                        {processingIndicator.currentlyPreviewingCounts > 0 && ` • ${processingIndicator.currentlyPreviewingCounts} previewing counts`}
                                        {processingIndicator.currentlyProcessing > 0 && ` • ${processingIndicator.currentlyProcessing} processing`}
                                    </Text>
                                </Group>
                            </Alert>
                        )}

                        <Table.ScrollContainer minWidth={800}>
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th w={48}>
                                            <Checkbox
                                                indeterminate={selectAllState.someSelected}
                                                checked={selectAllState.allSelected}
                                                onChange={(e) => handleSelectAllBulkGroups(e.currentTarget.checked)}
                                                disabled={loadingBulkGroups || processingBulkGroups}
                                            />
                                        </Table.Th>
                                        <Table.Th fw={700}>Group Name</Table.Th>
                                        <Table.Th fw={700}>LegacyFilter</Table.Th>
                                        <Table.Th fw={700}>FilterScope</Table.Th>
                                        <Table.Th fw={700}>Converted</Table.Th>
                                        <Table.Th fw={700} ta="center">Managed</Table.Th>
                                        <Table.Th fw={700}>Filter</Table.Th>
                                        <Table.Th fw={700} ta="center">Members</Table.Th>
                                        <Table.Th fw={700} ta="center">Warnings</Table.Th>
                                        <Table.Th fw={700} ta="center">Status</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {loadingBulkGroups && bulkGroups.length === 0 ? (
                                        <Table.Tr>
                                            <Table.Td colSpan={10} ta="center" py="xl">
                                                <Loader />
                                            </Table.Td>
                                        </Table.Tr>
                                    ) : bulkGroups.length === 0 ? (
                                        <Table.Tr>
                                            <Table.Td colSpan={10} ta="center" py="md">
                                                <Text size="sm" c="dimmed">
                                                    No groups found on this page.
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    ) : (
                                        bulkGroups.map((group) => {
                                            const status = bulkGroupStatuses.get(group.id);
                                            const isSelected = selectedBulkGroups.has(group.id);
                                            const hasFilter = Boolean(group.filterMatch && typeof group.filterMatch === 'string' && group.filterMatch.trim() !== '');
                                            const hasFilterScope = Boolean(group.filterScope && typeof group.filterScope === 'string' && group.filterScope.trim() !== '');
                                            const isManagedWithFilter = Boolean(group.isManaged && hasFilter && hasFilterScope);

                                            let isDisabled = processingBulkGroups || status?.status === 'processing';
                                            if (!group.needsFilterUpdate) {
                                                isDisabled = isDisabled || isManagedWithFilter;
                                            }

                                            let effectiveStatus = status;

                                            if (isManagedWithFilter && !status) {
                                                const filterTestComplete = !group.filterTestWarning || group.filterTestWarning !== 'Testing...';
                                                const filterTestPassed = filterTestComplete && !group.filterTestWarning;

                                                if (filterTestPassed) {
                                                    const groupCountText = group.filterTestGroupCount !== undefined
                                                        ? `\nGroupCount: ${group.filterTestGroupCount}`
                                                        : '';
                                                    effectiveStatus = {
                                                        status: 'success' as const,
                                                        message: `Group is managed and has a filter - already done${groupCountText}`
                                                    };
                                                } else if (group.filterTestWarning === 'Testing...') {
                                                    effectiveStatus = {
                                                        status: 'processing' as const,
                                                        message: 'Testing filter...'
                                                    };
                                                } else if (group.filterTestWarning) {
                                                    effectiveStatus = {
                                                        status: 'warning' as const,
                                                        message: `Filter test failed: ${group.filterTestWarning}`
                                                    };
                                                }
                                            }

                                            return (
                                                <Table.Tr
                                                    key={group.id}
                                                    style={{
                                                        backgroundColor: isSelected ? 'var(--mantine-color-default-hover)' : undefined
                                                    }}
                                                >
                                                    <Table.Td>
                                                        <Group gap="xs" align="center">
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={(e) => handleSelectBulkGroup(group.id, e.currentTarget.checked)}
                                                                disabled={isDisabled}
                                                                size="sm"
                                                            />
                                                            <Tooltip label="Refresh row">
                                                                <ActionIcon
                                                                    size="sm"
                                                                    variant="subtle"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRefreshRow(group.id);
                                                                    }}
                                                                    disabled={loadingBulkGroups || processingBulkGroups}
                                                                >
                                                                    <IconRefresh size={16} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                            {onViewDetails && (
                                                                <Tooltip label="View details">
                                                                    <ActionIcon
                                                                        size="sm"
                                                                        variant="subtle"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onViewDetails(group.id);
                                                                        }}
                                                                        disabled={loadingBulkGroups || processingBulkGroups}
                                                                    >
                                                                        <IconEye size={16} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </Group>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="sm">{group.displayName}</Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text
                                                            size="sm"
                                                            ff="monospace"
                                                            fz="xs"
                                                            style={{
                                                                wordBreak: 'break-word',
                                                                maxWidth: '300px'
                                                            }}
                                                        >
                                                            {group.legacyFilter || '—'}
                                                        </Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Text size="sm">{group.filterScope || '—'}</Text>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {editingConvertedFilter === group.id ? (
                                                            <TextInput
                                                                autoFocus
                                                                w="100%"
                                                                size="sm"
                                                                value={editedConvertedFilters.get(group.id) || group.optimizedFilter || group.convertedFilter || ''}
                                                                onChange={(e) => {
                                                                    const newMap = new Map(editedConvertedFilters);
                                                                    newMap.set(group.id, e.currentTarget.value);
                                                                    setEditedConvertedFilters(newMap);
                                                                }}
                                                                onBlur={() => handleEditedFilterBlur(group.id)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                                        e.preventDefault();
                                                                        handleEditedFilterBlur(group.id);
                                                                    } else if (e.key === 'Escape') {
                                                                        setEditingConvertedFilter(null);
                                                                        const newMap = new Map(editedConvertedFilters);
                                                                        newMap.delete(group.id);
                                                                        setEditedConvertedFilters(newMap);
                                                                    }
                                                                }}
                                                                styles={{
                                                                    input: {
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '0.75rem',
                                                                        padding: '4px 8px'
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <Group gap="xs" align="center">
                                                                {group._loading?.converted || group._loading?.optimized ? (
                                                                    <Loader size={16} />
                                                                ) : (
                                                                    <Text
                                                                        size="sm"
                                                                        component="span"
                                                                        ff="monospace"
                                                                        fz="xs"
                                                                        onClick={() => setEditingConvertedFilter(group.id)}
                                                                        style={{
                                                                            wordBreak: 'break-word',
                                                                            maxWidth: '300px',
                                                                            cursor: 'pointer',
                                                                            flex: 1,
                                                                            padding: '2px 4px',
                                                                            borderRadius: 4
                                                                        }}
                                                                    >
                                                                        {editedConvertedFilters.get(group.id) || group.optimizedFilter || group.convertedFilter || '—'}
                                                                    </Text>
                                                                )}
                                                                {group.attributeWarning && group.attributeWarning.length > 0 && (
                                                                    <Tooltip label={`Invalid attributes: ${group.attributeWarning.join(', ')}`}>
                                                                        <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                    </Tooltip>
                                                                )}
                                                            </Group>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td ta="center">
                                                        {group.isManaged ? (
                                                            <Tooltip label="Group is managed">
                                                                <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip label="Group is not managed">
                                                                <Text size="sm" c="dimmed">—</Text>
                                                            </Tooltip>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td>
                                                        {group.filterMatch ? (
                                                            <Text
                                                                size="sm"
                                                                ff="monospace"
                                                                fz="xs"
                                                                style={{
                                                                    wordBreak: 'break-all',
                                                                    maxWidth: '300px'
                                                                }}
                                                            >
                                                                {group.filterMatch}
                                                            </Text>
                                                        ) : (
                                                            <Text size="sm" c="dimmed">—</Text>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td ta="center">
                                                        {group.member && Array.isArray(group.member) && group.member.length > 0 ? (
                                                            <Group gap="xs" align="center">
                                                                <Tooltip
                                                                    label={
                                                                        <Box>
                                                                            <Text size="xs" component="span" style={{ display: 'block' }} mb={4}>
                                                                                First 5 IDs:
                                                                            </Text>
                                                                            {group.member.slice(0, 5).map((memberId: any, idx: number) => {
                                                                                const id = typeof memberId === 'string' ? memberId : (memberId.value || memberId.Id || memberId.id || memberId);
                                                                                return (
                                                                                    <Text key={idx} size="xs" component="span" ff="monospace" style={{ display: 'block', fontSize: '0.7rem' }}>
                                                                                        {id}
                                                                                    </Text>
                                                                                );
                                                                            })}
                                                                            {group.member.length > 5 && (
                                                                                <Text size="xs" component="span" style={{ display: 'block' }} mt={4}>
                                                                                    ...and {group.member.length - 5} more
                                                                                </Text>
                                                                            )}
                                                                        </Box>
                                                                    }
                                                                >
                                                                    <Text size="sm">{group.member.length}</Text>
                                                                </Tooltip>
                                                                <Tooltip label="Copy all member IDs to clipboard">
                                                                    <ActionIcon
                                                                        size="sm"
                                                                        variant="subtle"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleCopyMemberIds(group.member || []);
                                                                        }}
                                                                    >
                                                                        <IconCopy size={16} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            </Group>
                                                        ) : (
                                                            <Text size="sm" c="dimmed">0</Text>
                                                        )}
                                                    </Table.Td>
                                                    <Table.Td ta="center">
                                                        <Group gap="xs" align="center" justify="center" wrap="wrap">
                                                            {group.nameWarning && (
                                                                <Tooltip label={group.nameWarning}>
                                                                    <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                </Tooltip>
                                                            )}
                                                            {group.filterTestWarning && (
                                                                <Tooltip label={group.filterTestWarning === 'Testing...'
                                                                    ? 'Testing filter...'
                                                                    : `Filter test failed: ${group.filterTestWarning}`}>
                                                                    {group.filterTestWarning === 'Testing...' ? (
                                                                        <Loader size={16} />
                                                                    ) : (
                                                                        <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                                    )}
                                                                </Tooltip>
                                                            )}
                                                            {group.needsFilterUpdate && (
                                                                <Tooltip
                                                                    label={
                                                                        (() => {
                                                                            const hasExisting = group.existingFilterCount !== undefined;
                                                                            const hasOptimized = group.optimizedFilterCount !== undefined;
                                                                            if (hasExisting && hasOptimized) {
                                                                                return `Filter needs update - optimized version available\nExisting filter count: ${group.existingFilterCount}\nOptimized filter count: ${group.optimizedFilterCount}`;
                                                                            } else if (hasOptimized) {
                                                                                return `Filter needs update - optimized version available\nOptimized filter count: ${group.optimizedFilterCount}`;
                                                                            } else if (hasExisting) {
                                                                                return `Filter needs update - optimized version available\nExisting filter count: ${group.existingFilterCount}`;
                                                                            } else {
                                                                                return 'Filter needs update - optimized version available';
                                                                            }
                                                                        })()
                                                                    }
                                                                >
                                                                    <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
                                                                </Tooltip>
                                                            )}
                                                            {!group.nameWarning && !group.filterTestWarning && !group.needsFilterUpdate && (
                                                                group.optimizedFilterCount !== undefined ? (
                                                                    <Text size="sm" c="dimmed">
                                                                        Count: {group.optimizedFilterCount}
                                                                    </Text>
                                                                ) : (
                                                                    <Text size="sm" c="dimmed">—</Text>
                                                                )
                                                            )}
                                                        </Group>
                                                    </Table.Td>
                                                    <Table.Td ta="center">
                                                        {effectiveStatus?.status === 'processing' && (
                                                            <Loader size={16} />
                                                        )}
                                                        {effectiveStatus?.status === 'success' && (
                                                            <Tooltip label={effectiveStatus.message || 'Done'}>
                                                                <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />
                                                            </Tooltip>
                                                        )}
                                                        {effectiveStatus?.status === 'error' && (
                                                            <Tooltip label={effectiveStatus.message}>
                                                                <IconAlertCircle size={16} color="var(--mantine-color-red-6)" />
                                                            </Tooltip>
                                                        )}
                                                        {effectiveStatus?.status === 'warning' && (
                                                            <Tooltip label={effectiveStatus.message}>
                                                                <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                            </Tooltip>
                                                        )}
                                                        {group.hasSet && !effectiveStatus && (
                                                            <Tooltip label="Group has an existing set that will be deleted during conversion">
                                                                <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                                            </Tooltip>
                                                        )}
                                                        {!group.hasSet && !effectiveStatus && (
                                                            <Text size="sm" c="dimmed">—</Text>
                                                        )}
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })
                                    )}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>

                        {bulkGroupsTotalResults !== null && (
                            <Group justify="center" mt="md" gap="md" align="center">
                                <Select
                                    size="xs"
                                    value={bulkGroupsRowsPerPage.toString()}
                                    onChange={handleBulkGroupsRowsPerPageChange}
                                    data={rowsPerPageOptions}
                                    w={80}
                                />
                                <Pagination
                                    total={totalPages}
                                    value={bulkGroupsPage + 1}
                                    onChange={(page) => handleBulkGroupsPageChange(null, page - 1)}
                                    size="sm"
                                    withEdges
                                />
                            </Group>
                        )}

                        {processingIndicator.hasActiveWork && (
                            <Alert color="blue" mt="md" mb="md">
                                <Group gap="xs" align="center">
                                    <Loader size={16} />
                                    <Text size="sm">
                                        {loadingBulkGroups ? 'Loading' : 'Updating'}: <strong>{processingIndicator.totalStillProcessing}</strong> of <strong>{processingIndicator.totalGroups}</strong> remaining
                                        {processingIndicator.completed > 0 && ` (${processingIndicator.completed} completed)`}
                                        {processingIndicator.currentlyConvertingFilters > 0 && ` • ${processingIndicator.currentlyConvertingFilters} converting filters`}
                                        {processingIndicator.currentlyPreviewingCounts > 0 && ` • ${processingIndicator.currentlyPreviewingCounts} previewing counts`}
                                        {processingIndicator.currentlyProcessing > 0 && ` • ${processingIndicator.currentlyProcessing} processing`}
                                    </Text>
                                </Group>
                            </Alert>
                        )}

                        <Group justify="flex-end" align="center" mt="md">
                            <Group align="center" gap="xs">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleReloadBulkGroups}
                                    disabled={loadingBulkGroups}
                                    leftSection={loadingBulkGroups ? <Loader size={16} /> : <IconRefresh size={16} />}
                                >
                                    Reload
                                </Button>
                                <Switch
                                    label="Exclude Managed"
                                    checked={excludeManaged}
                                    onChange={(e) => setExcludeManaged(e.currentTarget.checked)}
                                />
                                <Switch
                                    label="Exclude Has Filter"
                                    checked={excludeHasFilter}
                                    onChange={(e) => setExcludeHasFilter(e.currentTarget.checked)}
                                />
                                <Switch
                                    label="Exclude JobCodeMemberOf"
                                    checked={excludeJobCodeMemberOf}
                                    onChange={(e) => setExcludeJobCodeMemberOf(e.currentTarget.checked)}
                                />
                                {bulkGroups.filter(g => selectedBulkGroups.has(g.id) && g.needsFilterUpdate).length > 0 && !processingBulkGroups && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleUpdateFilters}
                                        disabled={selectedBulkGroups.size === 0}
                                        leftSection={<IconRefresh size={16} />}
                                    >
                                        Update Filters ({bulkGroups.filter(g => selectedBulkGroups.has(g.id) && g.needsFilterUpdate).length})
                                    </Button>
                                )}
                                {processingBulkGroups ? (
                                    <Button
                                        color="red"
                                        size="sm"
                                        onClick={handleCancelBulkProcessing}
                                        leftSection={<IconX size={16} />}
                                    >
                                        Cancel
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        onClick={handleProcessBulkGroups}
                                        disabled={selectedBulkGroups.size === 0}
                                        leftSection={<IconPlayerPlay size={16} />}
                                    >
                                        Convert Groups ({selectedBulkGroups.size})
                                    </Button>
                                )}
                            </Group>
                        </Group>
                    </>
                ) : (
                    <Paper withBorder p="md">
                        <Title order={5} mb="xs">
                            Raw Views
                        </Title>
                        <Text size="sm" c="dimmed" mb="md">
                            Debug information and raw data views.
                        </Text>

                        <Box mb="md">
                            <Text fw={600} size="sm" mb="xs">
                                Effective Filter
                            </Text>
                            <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
                                <Text size="sm" component="pre" ff="monospace" fz="xs" m={0} style={{ whiteSpace: 'pre-wrap' }}>
                                    {effectiveFilter}
                                </Text>
                            </Paper>
                        </Box>

                        <Box mb="md">
                            <Text fw={600} size="sm" mb="xs">
                                Current State
                            </Text>
                            <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
                                <Text size="sm"><strong>Total Results:</strong> {bulkGroupsTotalResults ?? 'N/A'}</Text>
                                <Text size="sm"><strong>Current Page:</strong> {bulkGroupsPage + 1}</Text>
                                <Text size="sm"><strong>Groups Loaded:</strong> {bulkGroups.length}</Text>
                                <Text size="sm"><strong>Selected:</strong> {selectedBulkGroups.size}</Text>
                                <Text size="sm"><strong>Loading:</strong> {loadingBulkGroups ? 'Yes' : 'No'}</Text>
                                <Text size="sm"><strong>Processing:</strong> {processingBulkGroups ? 'Yes' : 'No'}</Text>
                            </Paper>
                        </Box>

                        {processingStats && (
                            <Box mb="md">
                                <Text fw={600} size="sm" mb="xs">
                                    Processing Stats
                                </Text>
                                <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
                                    <Text size="sm" component="pre" ff="monospace" fz="xs" m={0} style={{ whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(processingStats, null, 2)}
                                    </Text>
                                </Paper>
                            </Box>
                        )}

                        {conversionStats && (
                            <Box mb="md">
                                <Text fw={600} size="sm" mb="xs">
                                    Conversion Stats
                                </Text>
                                <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
                                    <Text size="sm" component="pre" ff="monospace" fz="xs" m={0} style={{ whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(conversionStats, null, 2)}
                                    </Text>
                                </Paper>
                            </Box>
                        )}
                    </Paper>
                )}
            </Paper>
        </Box>
    );
};
